import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';
import NullComponent from 'components/shared/NullComponent';
import { StyledClearButton, StyledDropdownButton } from './SelectField.styles';
import { propTypes, defaultProps, propDescriptions } from './SelectField.props';

import { colours, fonts } from 'themes/default';

const ClearButton = ({ innerProps: { ref, ...restInnerProps } }) => <StyledClearButton size={18} ref={ref} {...restInnerProps} />;
ClearButton.propTypes = {
  innerProps: PropTypes.shape({
    ref: PropTypes.element
  })
};

const DropdownButton = ({ innerProps: { ref, ...restInnerProps }, selectProps }) => (
  <StyledDropdownButton menuIsOpen={selectProps.menuIsOpen} ref={ref} {...restInnerProps} />
);
DropdownButton.propTypes = {
  innerProps: PropTypes.shape({
    ref: PropTypes.element
  }),
  selectProps: PropTypes.shape({
    menuIsOpen: PropTypes.bool
  })
};

const NoOptionsMessage = (props) => {
  const { content } = props;

  return <components.NoOptionsMessage {...props}>{content}</components.NoOptionsMessage>;
};

NoOptionsMessage.propTypes = {
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.element])
};

class SelectField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      menuIsOpen: false
    };
  }

  render() {
    const {
      id,
      value,
      options,
      multiple,
      loadOptions,
      onChange,
      inputRef,
      disabled,
      async,
      creatable,
      openMenu,
      hideIndicators,
      autoFocus,
      noOptionsMessage,
      onBlur,
      onFocus,
      onLoad,
      fixDropdownPosition,
      className
    } = this.props;

    const customStyles = {
      container: (base) => ({
        ...base,
        width: '100%',
        outline: 'none'
      }),
      control: (_, state) => {
        const focusStyles = state.isFocused
          ? {
              boxShadow: `0 0 0 1px ${colours.Core.CoreNavy}`
            }
          : {};

        const backgroundColor = state.isDisabled ? colours.Greyscale.SilverGrey : colours.Greyscale.White;

        return {
          color: colours.Core.CoreNavy.toString(),
          cursor: 'text',
          border: `1px solid ${colours.Shades.TintBlue10.toString()}`,
          display: 'flex',
          height: '30px',
          fontFamily: fonts.fontFamily,
          backgroundColor: backgroundColor.toString(),
          justifyContent: 'space-between',
          borderRadius: 0.35,
          ...focusStyles
        };
      },
      input: (base, state) => ({
        ...base,
        color: colours.Core.CoreNavy.toString(),
        margin: '0 2px'
      }),
      valueContainer: (base) => ({
        ...base,
        padding: '0 8px'
      }),
      menu: (base) => {
        const positionProps = fixDropdownPosition
          ? {
              top: 'initial',
              position: 'fixed',
              width: 'inherit',
              zIndex: 999
            }
          : {};

        return {
          ...base,
          ...positionProps
        };
      },
      singleValue: (base) => ({
        ...base,
        color: colours.Core.CoreNavy.toString()
      }),
      option: (_, state) => {
        let backgroundColor = colours.Greyscale.White.toString();
        if (state.isSelected) {
          backgroundColor = colours.Greyscale.MistGrey.toString();
        }

        if (state.isFocused) {
          backgroundColor = colours.Shades.TintBlue10.toString();
        }

        return {
          backgroundColor,
          cursor: 'pointer',
          color: colours.Greyscale.Charcoal.toString(),
          padding: '0.6em 0.8em'
        };
      }
    };

    let valueToOption;
    if (value) {
      if (multiple) {
        valueToOption = value.map(
          (val) =>
            options.find((option) => option.value === val) || {
              label: val,
              value: val
            }
        );
      } else if (options) {
        valueToOption = options.find((option) => option.value === value);
      }
    }

    const defaultSelectProps = {
      id,
      inputId: `${id}-input`,
      className,
      loadOptions,
      isMulti: multiple,
      onChange: (selected) => {
        if (multiple) {
          onChange(selected.map((option) => option.value));
        } else {
          onChange(selected.value);
        }

        if (openMenu) {
          this.setState({ menuIsOpen: false });
        }
      },
      onKeyDown: (e) => {
        if (!openMenu && e.key === 'Enter' && !this.inputRef.state.menuIsOpen) {
          e.preventDefault();
          this.inputRef.setState({ menuIsOpen: true });
        }
        const { menuIsOpen } = this.state;
        if (menuIsOpen && (e.key === 'Tab' || e.key === 'Enter')) {
          const inputs = [...document.querySelectorAll('input')];
          const nextInput = inputs[inputs.indexOf(document.activeElement) + 1];
          if (nextInput) {
            nextInput.focus();
          }
        }
      },
      onBlur: () => onBlur(value),
      onFocus: () => onFocus(value),
      options,
      ref: (ref) => {
        this.inputRef = ref;
        inputRef(ref);
        onLoad(value);
      },
      placeholder: '',
      value: valueToOption,
      isDisabled: disabled,
      components: {
        ClearIndicator: (props) => <ClearButton {...props} />,
        DropdownIndicator: (props) => <DropdownButton {...props} />,
        NoOptionsMessage: (props) => <NoOptionsMessage {...props} content={noOptionsMessage} />
      },
      styles: customStyles,
      autoFocus,
      'data-id': 'SelectField'
    };

    if (!value || value === '') {
      defaultSelectProps.value = '';
    }

    if (openMenu) {
      defaultSelectProps.onInputChange = (inputValue, { action }) => {
        if (action === 'input-change') {
          this.setState({ menuIsOpen: openMenu(inputValue) });
        }
      };
      defaultSelectProps.onBlur = () => {
        this.setState({ menuIsOpen: false });
      };
      defaultSelectProps.openMenuOnClick = false;

      const { menuIsOpen } = this.state;
      defaultSelectProps.menuIsOpen = menuIsOpen;
    }

    if (hideIndicators) {
      defaultSelectProps.components.IndicatorsContainer = NullComponent;
    }

    if (async && creatable) {
      return <AsyncCreatableSelect {...defaultSelectProps} />;
    }
    if (async) {
      return <AsyncSelect {...defaultSelectProps} />;
    }
    if (creatable) {
      return <CreatableSelect {...defaultSelectProps} />;
    }

    return <Select {...defaultSelectProps} />;
  }
}

SelectField.propTypes = propTypes;
SelectField.defaultProps = defaultProps;
SelectField.propDescriptions = propDescriptions;

export default SelectField;
